/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useState, useRef, useEffect} from 'react'
import {PageTitle} from '../../../../_designereyes/layout/core'
import axios from 'axios'
import {Button} from 'primereact/button'
import {Toast} from 'primereact/toast'
import {DataTable, DataTableStateEvent} from 'primereact/datatable'
import {Column} from 'primereact/column'
import {Card} from 'primereact/card'
import {Dialog} from 'primereact/dialog'
import 'primereact/resources/primereact.min.css'
import {API_URL} from '../../../modules/auth/core/_requests'
import {SortOrder, FilterMatchMode} from 'primereact/api'
import './ProductImagesFetcherWrapper.css'

const ProductImagesFetcher: FC = () => {
  const toast = useRef<Toast>(null)
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false)
  const [isDownloadingZip, setIsDownloadingZip] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [searchType, setSearchType] = useState('none') // 'none', 'brand', 'model', 'upc'
  const [imagesData, setImagesData] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedImages, setSelectedImages] = useState<any[]>([])
  const [showFormatDialog, setShowFormatDialog] = useState(false)
  const [selectedFormat, setSelectedFormat] = useState('shopify') // default format
  const [downloadType, setDownloadType] = useState<'excel' | 'zip' | null>(null)

  useEffect(() => {
    handleSearch() // This will trigger a search when the component mounts
  }, [])

  // Add this function to handle selection
  const onSelectionChange = (e: any) => {
    setSelectedImages(e.value)
  }

  const handleSearch = async () => {
    setLoading(true)
    try {
      // Convert the single search input into the appropriate filter structure
      const searchFilters = {
        upc: searchType === 'upc' ? searchText : '',
        model: searchType === 'model' ? searchText : '',
        brand: searchType === 'brand' ? searchText : '',
      }

      const response = await axios.post(`${API_URL}/api/product-images/retrieve`, searchFilters)
      setImagesData(response.data.data || [])
      setSelectedImages([])
    } catch (error) {
      console.error('Error fetching products:', error)
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Unable to fetch products',
        life: 3000,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadWithFormat = async () => {
    // Close dialog immediately
    setShowFormatDialog(false)

    try {
      if (downloadType === 'excel') {
        setIsDownloadingExcel(true)
      } else if (downloadType === 'zip') {
        setIsDownloadingZip(true)
      }

      const imageIds = selectedImages.map((img) => img.ImageID)
      const endpoint = downloadType === 'excel' ? 'download-excel' : 'download-zip'

      const response = await axios.post(
        `${API_URL}/api/product-images/retrieve/${endpoint}`,
        {
          imageIds,
          format: selectedFormat,
        },
        {responseType: 'blob'}
      )

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      const extension = downloadType === 'excel' ? 'xlsx' : 'zip'
      link.setAttribute('download', `product-images.${extension}`)
      document.body.appendChild(link)
      link.click()
      link.remove()

      // Reset download type
      setDownloadType(null)
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: `Failed to download ${downloadType?.toUpperCase()} file`,
        life: 3000,
      })
    } finally {
      if (downloadType === 'excel') {
        setIsDownloadingExcel(false)
      } else if (downloadType === 'zip') {
        setIsDownloadingZip(false)
      }
    }
  }

  const handleCancelDialog = () => {
    setShowFormatDialog(false)
    setDownloadType(null) // Reset download type when canceling
  }

  const handleExcelDownload = () => {
    setDownloadType('excel')
    setShowFormatDialog(true)
  }

  const handleZipDownload = () => {
    setDownloadType('zip')
    setShowFormatDialog(true)
  }

  return (
    <div className='card shadow-sm'>
      <Toast ref={toast} />
      <div className='card-header d-flex align-items-center mb-2'>
        <h2 className='card-title m-0 mb-0'>Product Images Fetcher</h2>
      </div>
      <div className='mb-3 mt-3 p-4 ps-2 pe-2'>
        <div className='container-fluid'>
          {/* Top section with radio buttons and download buttons */}
          <div className='mb-3 d-flex justify-content-between align-items-center'>
            {/* Left side - Radio buttons */}
            <div className='gap-4 d-flex  align-items-center' data-testid="filter-radio-group">
              <div className='custom-control custom-radio d-flex  align-items-center'>
                <input
                  type='radio'
                  id='radioNone'
                  name='searchType'
                  className='custom-control-input'
                  checked={searchType === 'none'}
                  onChange={() => setSearchType('none')}
                  data-testid="radio-all-products"
                />
                <label className='custom-control-label ms-2' htmlFor='radioNone'>
                  All Products
                </label>
              </div>
              <div className='custom-control custom-radio d-flex  align-items-center'>
                <input
                  type='radio'
                  id='radioBrand'
                  name='searchType'
                  className='custom-control-input'
                  checked={searchType === 'brand'}
                  onChange={() => setSearchType('brand')}
                  data-testid="radio-filter-brand"
                />
                <label className='custom-control-label ms-2' htmlFor='radioBrand'>
                  By Brand
                </label>
              </div>
              <div className='custom-control custom-radio d-flex  align-items-center'>
                <input
                  type='radio'
                  id='radioModel'
                  name='searchType'
                  className='custom-control-input'
                  checked={searchType === 'model'}
                  onChange={() => setSearchType('model')}
                  data-testid="radio-filter-model"
                />
                <label className='custom-control-label ms-2' htmlFor='radioModel'>
                  By Model
                </label>
              </div>
              <div className='custom-control custom-radio d-flex  align-items-center'>
                <input
                  type='radio'
                  id='radioUPC'
                  name='searchType'
                  className='custom-control-input'
                  checked={searchType === 'upc'}
                  onChange={() => setSearchType('upc')}
                  data-testid="radio-filter-upc"
                />
                <label className='custom-control-label ms-2' htmlFor='radioUPC'>
                  By UPC
                </label>
              </div>
            </div>

            {/* Right side - Download buttons */}
            <div className='d-flex gap-2' data-testid="download-buttons-group">
              <Button
                label={isDownloadingExcel ? 'Downloading...' : 'Download Excel'}
                icon='pi pi-file-excel'
                className='p-button-success'
                onClick={handleExcelDownload}
                disabled={selectedImages.length === 0 || isDownloadingExcel}
                loading={isDownloadingExcel}
                data-testid="download-excel-button"
              />
              <Button
                label={isDownloadingZip ? 'Downloading...' : 'Download ZIP'}
                icon='pi pi-download'
                className='p-button-primary'
                onClick={handleZipDownload}
                disabled={selectedImages.length === 0 || isDownloadingZip}
                loading={isDownloadingZip}
                data-testid="download-zip-button"
              />
            </div>
          </div>

          {/* Bottom section with search input and button */}
          <div className='row g-3 mt-2'>
            <div className='col-md-9'>
              <input
                type='text'
                className='form-control'
                placeholder={`Search by ${searchType === 'none' ? 'all fields' : searchType}...`}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{height: '45px'}}
                data-testid="search-input"
              />
            </div>
            <div className='col-md-3'>
              <Button
                label='Update Search'
                icon='pi pi-search'
                className='w-100 d-flex text-center justify-content-center gap-2'
                style={{height: '45px'}}
                onClick={handleSearch}
                data-testid="update-search-button"
              />
            </div>
          </div>
        </div>
      </div>

      <div className='card-body bg-body text-body mt-4 ps-1 pe-1'>
        {/* DataTable with simplified props */}
        <DataTable
          value={imagesData}
          loading={loading}
          scrollable
          scrollHeight='70vh'
          paginator stripedRows rowHover
          rows={20}
          rowsPerPageOptions={[ 20, 40, 100, imagesData.length]}
          className='mt-1'
          selection={selectedImages}
          onSelectionChange={onSelectionChange}
          selectionMode='multiple'
          dataKey='ImageID'
          data-testid="product-images-table"
        >
          <Column selectionMode='multiple' headerStyle={{width: '2rem'}} exportable={false} />
          <Column
            key='url'
            field='url'
            header='URL'
            style={{width: '50px'}}
            body={(rowData) => {
              if (rowData.url) {
                const hasProtocol =
                  rowData.url.startsWith('http://') || rowData.url.startsWith('https://')
                let fullUrl = ''

                if (hasProtocol) {
                  fullUrl = rowData.url
                } else if (rowData.url.startsWith('/')) {
                  fullUrl = `${window.location.origin}${rowData.url}`
                } else {
                  fullUrl = `https://${rowData.url}`
                }

                return (
                  <img
                    src={fullUrl}
                    alt={rowData.file_name}
                    style={{width: '120px', height: '60px', objectFit: 'fill'}}
                    onError={(e) => {
                      ;(e.currentTarget as HTMLImageElement).src = 'https://via.placeholder.com/50'
                    }}
                  />
                )
              }
              return null
            }}
          />
          <Column field='brand' header='Brand' sortable filter />
          <Column field='model' header='Model' sortable filter />
          <Column field='upc' header='UPC' sortable filter />
          <Column field='color' header='Color' sortable filter />
          <Column field='color_code' header='Color Code' sortable filter />
          <Column field='size' header='Size' sortable filter />
          <Column field='size_code' header='Size Code' sortable filter />
          <Column field='image_type' header='Image Type' sortable filter />
          <Column field='image_category' header='Image Category' sortable filter />
          <Column field='image_number' header='Image Number' sortable filter />
        </DataTable>
      </div>
      {selectedImages.length > 0 && (
        <div className='mt-2 d-flex gap-8' data-testid="selected-images-count">
          <span className='align-self-center'>Selected Images: {selectedImages.length}</span>
        </div>
      )}
      <Dialog
        visible={showFormatDialog}
        onHide={() => setShowFormatDialog(false)}
        header='Select Image Format'
        style={{width: '500px'}}
        modal
        className='format-selection-dialog'
        data-testid="format-selection-dialog"
        footer={
          <div>
            <Button
              label='Cancel'
              icon='pi pi-times'
              onClick={handleCancelDialog}
              className='p-button-text'
              data-testid="format-dialog-cancel-button"
            />
            <Button
              label='Download'
              icon='pi pi-download'
              onClick={handleDownloadWithFormat}
              autoFocus
              severity='success'
              data-testid="format-dialog-download-button"
            />
          </div>
        }
      >
        <div className='grid grid-cols-1 gap-4'>
          {[
            {
              id: 'shopify',
              title: 'Shopify Format',
              description: 'Optimized for Shopify product listings',
              specs: '2000x1000 pixels (2:1 aspect ratio)',
              icon: 'pi pi-shopping-cart',
            },
            {
              id: 'amazon',
              title: 'Amazon Format',
              description: 'Meets Amazon marketplace requirements',
              specs: '2000x2000 pixels (1:1 aspect ratio) with white background', // Updated from 2000x1000
              icon: 'pi pi-amazon',
            },
            {
              id: 'tiktok',
              title: 'TikTok Format',
              description: 'Perfect for TikTok Shop listings',
              specs: '1000x1000 pixels (1:1 aspect ratio)',
              icon: 'pi pi-video',
            },
          ].map((format) => (
            <div
              key={format.id}
              className={`format-option p-3 border-round cursor-pointer ${
                selectedFormat === format.id ? 'selected' : ''
              }`}
              onClick={() => setSelectedFormat(format.id)}
              data-testid={`format-option-${format.id}`}
            >
              <div className='flex align-items-center gap-3'>
                <div className='format-radio'>
                  <i className={`${format.icon} text-xl`}></i>
                </div>
                <div className='flex-1'>
                  <h3 className='m-0 mb-2'>{format.title}</h3>
                  <p className='m-0 text-sm text-500'>{format.description}</p>
                  <small className='text-primary'>{format.specs}</small>
                </div>
                <div className='format-check'>
                  <i
                    className={`pi ${
                      selectedFormat === format.id ? 'pi-check-circle text-success' : 'pi-circle'
                    } text-xl`}
                  ></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Dialog>
    </div>
  )
}

export const ProductImagesFetcherWrapper: FC = () => {
  return (
    <>
      <ProductImagesFetcher />
    </>
  )
}